import { migrationV1 } from './v1'
import { migrationV2 } from './v2'
import { migrationV3 } from './v3'
import { migrationV4 } from './v4'

/**
 * Migration context.
 *
 * Everything a migration receives is a live **native** IndexedDB handle inside
 * the `versionchange` transaction — not an `idb` wrapper. That is deliberate:
 * `idb` turns every request into a promise, and the only way to move records
 * inside a synchronous `run` is the raw `request.onsuccess` form. Handing a
 * migration the promise-shaped API would leave `await` as the obvious way to
 * read a record, and awaiting inside `versionchange` is precisely the mistake
 * the synchronous signature below exists to prevent (D-028).
 */
export interface MigrationContext {
  readonly database: IDBDatabase
  readonly transaction: IDBTransaction
}

/**
 * A single forward migration.
 *
 * `run` returns `void`, NOT `Promise<void>`, and that is the load-bearing part
 * of this design. IndexedDB commits a transaction as soon as the microtask
 * queue drains, so awaiting any non-IndexedDB promise inside `versionchange`
 * would commit the upgrade early and half-migrated. Making `run` synchronous
 * means such code cannot be written here at all (D-028).
 *
 * A migration that needs expensive or asynchronous preparation must do it in
 * the boot preflight, before the higher version is opened.
 */
export interface Migration {
  /** The schema version this migration produces. */
  readonly to: number
  readonly describe: string
  run(context: MigrationContext): void
}

/** Ordered registry. Append only; never renumber or edit a released migration. */
export const MIGRATIONS: readonly Migration[] = [migrationV1, migrationV2, migrationV3, migrationV4]

export interface RunMigrationsOptions {
  readonly database: IDBDatabase
  readonly transaction: IDBTransaction
  readonly oldVersion: number
  readonly newVersion: number
  readonly migrations: readonly Migration[]
}

/**
 * Apply every migration in `(oldVersion, newVersion]` in ascending order.
 *
 * If any step throws, the exception propagates out of the `upgrade` callback and
 * IndexedDB aborts the `versionchange` transaction, rolling back *every* step
 * atomically and leaving the stored version unchanged. We rely on that built-in
 * guarantee rather than attempting to undo work ourselves.
 */
export function runMigrations(options: RunMigrationsOptions): void {
  const { database, transaction, oldVersion, newVersion, migrations } = options

  const applicable = migrations.filter(
    (migration) => migration.to > oldVersion && migration.to <= newVersion,
  )
  const ordered = [...applicable].sort((left, right) => left.to - right.to)

  for (const migration of ordered) {
    migration.run({ database, transaction })
  }
}
